import {
  ArrowLeft,
  Building2,
  CheckCircle,
  Mail,
  Phone,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SupportTicketsPanel, type LinkedSupportTicket } from "../../components/SupportTicketsPanel";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Membership = {
  id: string;
  role: string;
  createdAt: string;
  orgId: string;
  orgName: string;
  orgSlug: string;
};

type UserDetail = {
  id: string;
  name: string;
  email: string | null;
  emailVerified: boolean;
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  firstName: string | null;
  lastName: string | null;
  image: string | null;
  isSuperAdmin: boolean | null;
  banned: boolean | null;
  banReason: string | null;
  banExpires: string | null;
  createdAt: string;
  updatedAt: string;
  memberships: Membership[];
  supportTickets: LinkedSupportTicket[];
};

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!id) return;
    fetch(`${API}/api/admin/users/${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [id]);

  async function patch(payload: object) {
    if (!id) return;
    setActing(true);
    try {
      const r = await fetch(`${API}/api/admin/users/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        const updated = await r.json();
        setData((prev) => (prev ? { ...prev, ...updated } : prev));
      }
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <p className="text-sm text-muted-foreground">Laster bruker...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <p className="text-sm text-destructive">Brukeren ble ikke funnet.</p>
      </div>
    );
  }

  const initials = [data.firstName, data.lastName]
    .filter(Boolean)
    .map((s) => s![0])
    .join("")
    .toUpperCase() || data.name[0]?.toUpperCase();

  const isBanned = data.banned === true;
  const isAdmin = data.isSuperAdmin === true;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/brukere")} className="mt-0.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/8 flex-shrink-0">
            <span className="text-lg font-bold text-primary">{initials}</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
                {data.name}
              </h1>
              {isAdmin && <Badge variant="accent">Super admin</Badge>}
              {isBanned && <Badge variant="destructive">Suspendert</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">{data.email ?? data.phoneNumber ?? "—"}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="oversikt">
            <TabsList>
              <TabsTrigger value="oversikt">Oversikt</TabsTrigger>
              <TabsTrigger value="support">Support ({data.supportTickets.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="oversikt" className="mt-6 space-y-6">
              <Card>
                <CardHeader><CardTitle className="text-sm font-semibold">Kontaktinfo</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <InfoRow
                    icon={<Mail className="h-4 w-4" />}
                    label="E-post"
                    value={data.email ?? "—"}
                    verified={data.email ? data.emailVerified : null}
                  />
                  <InfoRow
                    icon={<Phone className="h-4 w-4" />}
                    label="Telefon"
                    value={data.phoneNumber ?? "—"}
                    verified={data.phoneNumber ? data.phoneNumberVerified : null}
                  />
                  <div className="pt-2 border-t border-border flex justify-between text-xs text-muted-foreground">
                    <span>Registrert {formatDate(data.createdAt)}</span>
                    <span>Oppdatert {formatDate(data.updatedAt)}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <CardHeader><CardTitle className="text-sm font-semibold">Organisasjoner ({data.memberships.length})</CardTitle></CardHeader>
                {data.memberships.length === 0 ? (
                  <CardContent>
                    <p className="text-sm text-muted-foreground">Ikke medlem av noen organisasjoner</p>
                  </CardContent>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-y border-border bg-muted/40">
                        <th className="px-6 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organisasjon</th>
                        <th className="px-6 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rolle</th>
                        <th className="px-6 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Lagt til</th>
                        <th className="px-6 py-2.5" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.memberships.map((m) => (
                        <tr
                          key={m.id}
                          className="hover:bg-muted/20 transition-colors cursor-pointer"
                          onClick={() => navigate(`/organisasjoner/${m.orgId}`)}
                        >
                          <td className="px-6 py-3.5">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="font-medium">{m.orgName}</span>
                            </div>
                            <p className="text-xs text-muted-foreground font-mono pl-5.5">{m.orgSlug}</p>
                          </td>
                          <td className="px-6 py-3.5"><RoleBadge role={m.role} /></td>
                          <td className="px-6 py-3.5 text-muted-foreground hidden sm:table-cell">{formatDate(m.createdAt)}</td>
                          <td className="px-6 py-3.5">
                            <ArrowLeft className="h-3.5 w-3.5 text-muted-foreground ml-auto rotate-180" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="support" className="mt-6">
              <SupportTicketsPanel
                tickets={data.supportTickets}
                emptyLabel="Ingen supporthenvendelser er koblet til denne brukeren."
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right column — actions */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Tilgangsnivå</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Super admin</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Gir tilgang til admin-panelet</p>
                </div>
                <Button
                  size="sm"
                  variant={isAdmin ? "destructive" : "secondary"}
                  disabled={acting}
                  onClick={() => patch({ isSuperAdmin: !isAdmin })}
                  className="flex-shrink-0"
                >
                  {isAdmin ? (
                    <><ShieldAlert className="h-3.5 w-3.5 mr-1.5" />Fjern</>
                  ) : (
                    <><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Gi tilgang</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className={isBanned ? "border-destructive/40" : ""}>
            <CardHeader><CardTitle className="text-sm font-semibold">Kontostatus</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {isBanned && data.banReason && (
                <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2.5">
                  <span className="font-medium text-foreground">Årsak: </span>{data.banReason}
                </p>
              )}
              <Button
                size="sm"
                variant={isBanned ? "secondary" : "destructive"}
                disabled={acting}
                onClick={() => patch({ banned: !isBanned, banReason: isBanned ? null : undefined })}
                className="w-full"
              >
                {isBanned ? (
                  <><CheckCircle className="h-3.5 w-3.5 mr-1.5" />Gjenopprett konto</>
                ) : (
                  <><XCircle className="h-3.5 w-3.5 mr-1.5" />Suspender bruker</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  verified,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  verified: boolean | null;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm min-w-0">
        <span className="text-muted-foreground flex-shrink-0">{icon}</span>
        <span className="text-muted-foreground flex-shrink-0">{label}</span>
        <span className="truncate font-medium">{value}</span>
      </div>
      {verified !== null && (
        verified
          ? <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
          : <XCircle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    owner: { label: "Eier", variant: "accent" },
    admin: { label: "Admin", variant: "warning" },
    member: { label: "Bruker", variant: "secondary" },
  };
  const { label, variant } = map[role] ?? { label: role, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
